import type { useSidebarV2Controller } from "./sidebarV2Controller";
import type { EnvironmentId } from "@cmmd-forge/contracts";
import { lookupProjectDisplay, type ProjectDisplayEntry } from "../projectDisplayIndex";
import { resolveThreadOrigin, type ThreadOrigin } from "../threadOrigin";
import {
  runtimeScopedSidebarKey,
  type SavedEnvironmentRuntimeIndex,
} from "../sidebarThreadRuntimeIdentity";

type Controller = ReturnType<typeof useSidebarV2Controller>;

/** Index each group name under both its raw and runtime-scoped member keys. */
export function buildProjectGroupNameByKey(
  entries: readonly {
    readonly displayName: string;
    readonly memberRefs: readonly {
      readonly environmentId: string;
      readonly projectId: string;
    }[];
  }[],
  environments: SavedEnvironmentRuntimeIndex,
): ReadonlyMap<string, string> {
  const names = new Map<string, string>();
  for (const entry of entries) {
    for (const member of entry.memberRefs) {
      const key = `${member.environmentId}:${member.projectId}`;
      const alias = runtimeScopedSidebarKey(member.environmentId, member.projectId, environments);
      if (!names.has(key)) names.set(key, entry.displayName);
      if (!names.has(alias)) names.set(alias, entry.displayName);
    }
  }
  return names;
}

/** Resolve group identity and the member project's own checkout for a thread row. */
export function buildRowThreadProps(
  props: {
    readonly projectDisplayNameByKey: ReadonlyMap<string, string>;
    readonly projectDisplayByKey: ReadonlyMap<string, ProjectDisplayEntry>;
    readonly savedEnvironmentRegistry: SavedEnvironmentRuntimeIndex;
    readonly resolveEnvironmentLabel: (environmentId: EnvironmentId) => string;
    readonly currentEnvironmentId?: EnvironmentId | null | undefined;
  },
  thread: NonNullable<ReturnType<Controller["threadByKey"]["get"]>>,
) {
  const memberKey = `${thread.environmentId}:${thread.projectId}`;
  const projectDisplay = lookupProjectDisplay(
    props.projectDisplayByKey,
    thread,
    props.savedEnvironmentRegistry,
  );
  return {
    projectCwd: projectDisplay?.cwd ?? null,
    projectCanonicalIdentity: projectDisplay?.canonicalIdentity,
    projectDisplayName:
      props.projectDisplayNameByKey.get(memberKey) ??
      props.projectDisplayNameByKey.get(
        runtimeScopedSidebarKey(
          thread.environmentId,
          thread.projectId,
          props.savedEnvironmentRegistry,
        ),
      ) ??
      null,
    environmentLabel: props.resolveEnvironmentLabel(thread.environmentId),
    threadOrigin: resolveThreadOrigin({
      threadEnvironmentId: thread.environmentId,
      currentEnvironmentId: props.currentEnvironmentId,
      savedEnvironments: props.savedEnvironmentRegistry,
    }) satisfies ThreadOrigin,
    linkedPrUrl: thread.linkedPullRequest?.url ?? null,
  };
}
